import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { AppShell, PageBody } from '@/components/AppShell'
import { AdminLayout } from '@/pages/admin/AdminLayout'
import { DatasetsPage } from '@/pages/admin/DatasetsPage'
import { FeedbackPage } from '@/pages/admin/FeedbackPage'
import { LinksPage } from '@/pages/admin/LinksPage'
import { ReviewersPage } from '@/pages/admin/ReviewersPage'
import { LanguagePage } from '@/pages/reviewer/LanguagePage'
import { ReportPage } from '@/pages/reviewer/ReportPage'
import { SummaryPage } from '@/pages/reviewer/SummaryPage'
import { VerifyPage } from '@/pages/reviewer/VerifyPage'

const rootRoute = createRootRoute({ component: Outlet })

/** Nothing lives at the root: you arrive here with a link or not at all. */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (
    <AppShell>
      <PageBody className="max-w-[560px] pt-16">
        <h1 className="font-serif text-[22px]">Weryfikacja osi stylu</h1>
        <p className="mt-3 text-[14px] leading-relaxed text-muted">
          Ta strona działa na zaproszenia. Jeśli masz od nas link, otwórz go w całości.
          Panel administratora jest pod <code className="font-mono">/admin</code>.
        </p>
      </PageBody>
    </AppShell>
  ),
})

const reviewerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/r/$token',
  component: Outlet,
})

/**
 * The link opens on a board, not on a welcome page (owner, 2026-08-03).
 *
 * A reviewer who was sent a link already knows what they were asked for; a
 * page of preamble between them and the first position is a page nobody reads.
 * The stream itself decides which board that is: the first one on a fresh
 * link, wherever they stopped on a link they have opened before.
 */
const reviewerIndexRoute = createRoute({
  getParentRoute: () => reviewerRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/r/$token/verify',
      params: { token: params.token },
    })
  },
  component: () => null,
})

const reportRoute = createRoute({
  getParentRoute: () => reviewerRoute,
  path: 'report',
  component: ReportPage,
})

const verifyRoute = createRoute({
  getParentRoute: () => reviewerRoute,
  path: 'verify',
  // `i` keeps ordinary previous/next navigation compact. `id` is the stable,
  // human-readable evidence id used when we send a link to one exact board.
  validateSearch: (search: Record<string, unknown>): { i?: number; id?: string } => {
    const value = Number(search.i)
    const id = typeof search.id === 'string' ? search.id.trim() : ''
    return {
      ...(Number.isFinite(value) && value >= 0 ? { i: Math.floor(value) } : {}),
      ...(id ? { id } : {}),
    }
  },
  component: VerifyPage,
})

const summaryRoute = createRoute({
  getParentRoute: () => reviewerRoute,
  path: 'summary',
  component: SummaryPage,
})

/** The language sheet: one page under its own token, no board machinery. */
const languageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/l/$token',
  component: LanguagePage,
})

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminLayout,
})

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: DatasetsPage,
})

const adminReviewersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'reviewers',
  component: ReviewersPage,
})

const adminLinksRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'links',
  component: LinksPage,
})

const adminFeedbackRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'feedback',
  component: FeedbackPage,
})

const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  beforeLoad: () => {
    throw redirect({ to: '/' })
  },
  component: () => null,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  reviewerRoute.addChildren([
    reviewerIndexRoute,
    reportRoute,
    verifyRoute,
    summaryRoute,
  ]),
  languageRoute,
  adminRoute.addChildren([
    adminIndexRoute,
    adminReviewersRoute,
    adminLinksRoute,
    adminFeedbackRoute,
  ]),
  catchAllRoute,
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
